import type { ReactNode } from 'react'
import clsx from 'clsx'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { Card } from '../Card/Card'
import { Skeleton } from '../Skeleton/Skeleton'
import styles from './KpiTile.module.scss'

export type KpiTone = 'blue' | 'amber' | 'red' | 'green' | 'purple' | 'teal' | 'gray'

export interface KpiTileProps {
  label: string
  value: ReactNode
  unit?: string
  icon?: ReactNode
  tone?: KpiTone
  /** Percentage or absolute change vs. the previous period. Positive renders green. */
  delta?: number | null
  deltaLabel?: string
  /** Up to ~12 points rendered as a tiny sparkline. */
  trend?: number[]
  loading?: boolean
  className?: string
}

export function KpiTile({
  label,
  value,
  unit,
  icon,
  tone = 'gray',
  delta,
  deltaLabel = 'vs last week',
  trend,
  loading,
  className,
}: KpiTileProps) {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta)
  const up = hasDelta && delta >= 0
  const color = tone === 'gray' ? 'var(--vc-text-secondary)' : `var(--vc-${tone})`
  return (
    <Card className={clsx(styles.tile, styles[tone], className)} padding="none">
      <div className={styles.top}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <span>{label}</span>
      </div>
      <div className={styles.bottom}>
        <div>
          {loading ? (
            <Skeleton width={64} height={28} />
          ) : (
            <div className={styles.value}>
              {value}
              {unit && <span className={styles.unit}>{unit}</span>}
            </div>
          )}
          {hasDelta && !loading && (
            <div className={styles.delta}>
              <span className={clsx(styles.deltaPill, up ? styles.up : styles.down)}>
                {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                {Math.abs(delta)}
                {Number.isInteger(delta) ? '' : ''}
              </span>
              {deltaLabel}
            </div>
          )}
        </div>
        {trend && trend.length > 1 && !loading && <Sparkline points={trend} color={color} />}
      </div>
    </Card>
  )
}

export function Sparkline({ points, color, className }: { points: number[]; color: string; className?: string }) {
  const w = 84
  const h = 32
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const step = w / (points.length - 1)
  const coords = points.map((p, i) => [i * step, h - 3 - ((p - min) / range) * (h - 6)] as const)
  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${path} L${w},${h} L0,${h} Z`
  return (
    <svg className={clsx(styles.spark, className)} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={area} fill={color} opacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function KpiGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.grid, className)}>{children}</div>
}
